import React, { useEffect, useState } from 'react';
import { XMarkIcon, ArrowPathIcon, SparklesIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { getTrendAdvice, HypeAdvice } from '../../services/hype';

const TIER: Record<string, string> = {
  high: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  medium: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
  low: 'bg-slate-50 text-slate-600 ring-1 ring-slate-200',
};

function money(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return `${Math.round(n)}`;
}

const SCEN: { key: 'conservative' | 'expected' | 'optimistic'; label: string; cls: string }[] = [
  { key: 'conservative', label: 'Conservative', cls: 'border-gray-200' },
  { key: 'expected', label: 'Expected', cls: 'border-violet-300 ring-1 ring-violet-200' },
  { key: 'optimistic', label: 'Optimistic', cls: 'border-gray-200' },
];

export default function TrendDetailModal({ trendId, onClose }: { trendId: string; onClose: () => void }) {
  const [advice, setAdvice] = useState<HypeAdvice | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getTrendAdvice(trendId)
      .then(a => alive && setAdvice(a))
      .catch(e => alive && setError(e?.response?.data?.detail || 'Could not load advice.'));
    return () => { alive = false; };
  }, [trendId]);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-start justify-between rounded-t-2xl">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 truncate">
              {advice?.trend.label || 'Trend'}
            </h2>
            {advice && (
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-gray-500">Hype {advice.trend.hype_score.toFixed(0)}/100</span>
                <span className={`px-2 py-0.5 text-xs rounded-full ${TIER[advice.recommendation.engagement_tier] || TIER.low}`}>
                  {advice.recommendation.engagement_tier} engagement
                </span>
              </div>
            )}
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {!advice && !error && (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <ArrowPathIcon className="h-7 w-7 animate-spin mb-3" />
            <p className="text-sm">Building your content brief…</p>
          </div>
        )}
        {error && <div className="m-6 bg-red-50 border border-red-100 text-red-600 rounded-xl p-4 text-sm">{error}</div>}

        {advice && (
          <div className="px-6 py-5 space-y-6">
            {/* Summary */}
            <div>
              <p className="text-sm text-gray-700">{advice.recommendation.summary}</p>
              <p className="text-sm text-violet-700 mt-1">{advice.recommendation.why_now}</p>
            </div>

            {/* Content ideas */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <SparklesIcon className="h-4 w-4 text-violet-600" />
                <h3 className="text-sm font-semibold text-gray-900">What to create</h3>
                <span className="text-xs text-gray-400">· best: {advice.recommendation.best_format}</span>
              </div>
              <div className="space-y-3">
                {advice.recommendation.content_ideas.map((idea, i) => (
                  <div key={i} className="border border-gray-100 rounded-xl p-4 bg-gray-50/50">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2 py-0.5 text-xs font-medium bg-violet-100 text-violet-700 rounded-md">{idea.format}</span>
                      <span className="text-sm font-semibold text-gray-900">{idea.hook}</span>
                    </div>
                    <p className="text-xs text-gray-500 mb-2">{idea.angle}</p>
                    <p className="text-sm text-gray-600 italic">“{idea.example_caption}”</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Hashtags + tips */}
            <div className="flex flex-wrap gap-1.5">
              {advice.recommendation.hashtags.map(h => (
                <span key={h} className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-md">{h}</span>
              ))}
            </div>
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-sm text-amber-800">
              💡 {advice.recommendation.posting_tips}
            </div>

            {/* ROI */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <ChartBarIcon className="h-4 w-4 text-violet-600" />
                <h3 className="text-sm font-semibold text-gray-900">Projected reach &amp; ROI</h3>
                <span className="text-xs text-gray-400">· reach ×{advice.roi.reach_multiplier} from momentum</span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {SCEN.map(s => {
                  const v = advice.roi.scenarios[s.key];
                  return (
                    <div key={s.key} className={`rounded-xl border p-3 ${s.cls}`}>
                      <p className="text-xs font-medium text-gray-500 mb-2">{s.label}</p>
                      <p className="text-lg font-bold text-gray-900">{v.roi_pct.toFixed(0)}%<span className="text-xs font-normal text-gray-400"> ROI</span></p>
                      <dl className="mt-2 space-y-0.5 text-xs text-gray-500">
                        <div className="flex justify-between"><dt>Reach</dt><dd className="text-gray-700">{money(v.reach)}</dd></div>
                        <div className="flex justify-between"><dt>Leads</dt><dd className="text-gray-700">{v.leads}</dd></div>
                        <div className="flex justify-between"><dt>Customers</dt><dd className="text-gray-700">{v.customers}</dd></div>
                        <div className="flex justify-between"><dt>Revenue</dt><dd className="text-gray-700">{money(v.revenue)}</dd></div>
                      </dl>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400 mt-2">{advice.roi.disclaimer}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
